import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { X, Package } from 'lucide-react';
import { toast } from 'react-toastify';
import { competitors } from '../../config/competitors';

interface Product {
  id: string;
  name: string;
  ean: string;
  regularPrice: number;
  proPlusPrice?: number;
  isProPlus: boolean;
  mainImage?: string;
  status: 'approved' | 'pending';
}

interface PricebenchItem {
  id: string;
  productId: string;
  productName: string;
  productEan: string;
  regularPrice: number;
  proPlusPrice?: number;
  productImage?: string;
  isProPlus: boolean;
  competitorName: string;
  competitorCode: string;
  competitorUrl: string;
  competitorPrice?: number;
  lastUpdate?: Timestamp;
  promotionalPeriod?: {
    startDate: string;
    endDate: string;
  };
  salePrice?: number;
  sku?: string;
}

interface Props {
  onClose: () => void;
  onAdd: (itemData: any, id?: string) => void;
  selectedItem?: PricebenchItem | null;
}

export default function AddPricebenchModal({ onClose, onAdd, selectedItem }: Props) {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [competitorName, setCompetitorName] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [competitorPrice, setCompetitorPrice] = useState<number | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    const loadProducts = async () => {
      if (!currentUser?.companyId) {
        setError('Erro: ID da empresa não encontrado');
        return;
      }

      setLoading(true);
      setError('');
      try {
        const approvedProductsRef = collection(db, 'products');
        const approvedQuery = query(
          approvedProductsRef,
          where('companyId', '==', currentUser.companyId)
        );
        const approvedSnapshot = await getDocs(approvedQuery);
        const approvedProducts = approvedSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Product[];

        setProducts(approvedProducts);
        
        if (selectedItem) {
          setCompetitorName(selectedItem.competitorName);
          setCompetitorUrl(selectedItem.competitorUrl);
          setCompetitorPrice(selectedItem.competitorPrice || '');
          setSelectedProduct(selectedItem.productId);
        }
      } catch (err) {
        console.error('Erro ao carregar produtos:', err);
        setError('Erro ao carregar produtos');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [currentUser?.companyId, selectedItem]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser?.companyId) {
      toast.error('Erro: ID da empresa não encontrado');
      return;
    }

    if (!selectedProduct || !competitorName || !competitorUrl) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      const product = products.find(p => p.id === selectedProduct);
      if (!product) {
        toast.error('Produto não encontrado');
        return;
      }

      const competitor = Object.values(competitors).find(c => c.name === competitorName);
      if (!competitor) {
        toast.error('Competidor não encontrado');
        return;
      }

      const itemData = {
        productId: product.id,
        productName: product.name,
        productEan: product.ean,
        regularPrice: product.regularPrice,
        proPlusPrice: product.proPlusPrice,
        productImage: product.mainImage,
        isProPlus: product.isProPlus,
        competitorName,
        competitorCode: competitor.code,
        competitorUrl,
        competitorPrice: competitorPrice || null
      };

      // Se tiver selectedItem, é uma edição
      onAdd(itemData, selectedItem?.id);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      toast.error('Erro ao salvar item');
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.ean.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {selectedItem ? 'Editar Item' : 'Adicionar Novo Item'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Produto
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Pesquisar por nome ou EAN..."
                className="w-full p-2 pr-10 border rounded-lg focus:ring-primary focus:border-primary"
              />
              <Package className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {searchTerm && (
            <div className="mb-4 max-h-60 overflow-y-auto border rounded-lg">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className={`p-3 hover:bg-gray-50 cursor-pointer ${
                    selectedProduct === product.id ? 'bg-primary/10' : ''
                  }`}
                  onClick={() => setSelectedProduct(product.id)}
                >
                  <div className="flex items-center">
                    {product.mainImage ? (
                      <img
                        src={product.mainImage}
                        alt={product.name}
                        className="h-10 w-10 rounded-md object-cover mr-3"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-md bg-gray-100 flex items-center justify-center mr-3">
                        <Package className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-gray-500">
                        EAN: {product.ean}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecione o Competidor
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.values(competitors).map((competitor) => (
                <div
                  key={competitor.code}
                  className={`border rounded-lg p-4 ${
                    competitor.available 
                      ? `cursor-pointer transition-all ${
                          competitorName === competitor.name
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-200 hover:border-primary/50'
                        }`
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  onClick={() => {
                    if (competitor.available) {
                      setCompetitorName(competitor.name);
                    }
                  }}
                >
                  <img
                    src={competitor.logo}
                    alt={competitor.name}
                    className="w-full h-12 object-contain mb-2"
                  />
                  <p className="text-center text-sm font-medium">{competitor.name}</p>
                  {!competitor.available && (
                    <p className="text-center text-xs text-gray-500">Brevemente</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL do Produto no Concorrente
            </label>
            <input
              type="url"
              value={competitorUrl}
              onChange={(e) => setCompetitorUrl(e.target.value)}
              className="w-full p-2 border rounded-lg focus:ring-primary focus:border-primary"
              required
              placeholder="https://www.continente.pt/produto/..."
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preço no Concorrente
            </label>
            <input
              type="number"
              value={competitorPrice}
              onChange={(e) => setCompetitorPrice(e.target.value ? parseFloat(e.target.value) : '')}
              step="0.01"
              min="0"
              className="w-full p-2 border rounded-lg focus:ring-primary focus:border-primary"
              placeholder="Opcional - Será atualizado automaticamente"
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {selectedItem ? 'Atualizar' : 'Adicionar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
